/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { get, put } from '@/services/api/client';
import { ApiResponse } from '@/services/api/types';
import { UatItemDto, UatFeedbackDto, UatProgressDto, UatOrgSummaryDto } from '@/types/uat.types';
import axios from 'axios';
import { projectConfig } from '@/config/project.config';
import { useAuthStore } from '@/stores/authStore';

const ORG_BASE = '/uat';
const ADMIN_BASE = '/admin/uat';

export const uatService = {
  // OrgAdmin
  async getItems(): Promise<UatItemDto[]> {
    const response = await get<ApiResponse<UatItemDto[]>>(`${ORG_BASE}/items`);
    return response.data;
  },
  async updateStatus(code: string, status: string): Promise<UatItemDto> {
    const response = await put<ApiResponse<UatItemDto>>(`${ORG_BASE}/items/${code}/status`, { status });
    return response.data;
  },
  async getFeedback(code: string): Promise<UatFeedbackDto[]> {
    const response = await get<ApiResponse<UatFeedbackDto[]>>(`${ORG_BASE}/items/${code}/feedback`);
    return response.data;
  },
  async addFeedback(code: string, message: string, files?: File[]): Promise<UatFeedbackDto> {
    const formData = new FormData();
    formData.append('message', message);
    if (files) files.forEach(f => formData.append('files', f));
    const token = useAuthStore.getState().accessToken;
    const resp = await axios.post<ApiResponse<UatFeedbackDto>>(
      `${projectConfig.api.baseUrl}${ORG_BASE}/items/${code}/feedback`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` } }
    );
    return resp.data.data;
  },
  async getProgress(): Promise<UatProgressDto> {
    const response = await get<ApiResponse<UatProgressDto>>(`${ORG_BASE}/progress`);
    return response.data;
  },
  // SuperAdmin
  async getOrgSummaries(): Promise<UatOrgSummaryDto[]> {
    const response = await get<ApiResponse<UatOrgSummaryDto[]>>(`${ADMIN_BASE}/orgs`);
    return response.data;
  },
  async getOrgItems(orgId: string): Promise<UatItemDto[]> {
    const response = await get<ApiResponse<UatItemDto[]>>(`${ADMIN_BASE}/orgs/${orgId}/items`);
    return response.data;
  },
  async getOrgFeedback(orgId: string, code: string): Promise<UatFeedbackDto[]> {
    const response = await get<ApiResponse<UatFeedbackDto[]>>(`${ADMIN_BASE}/orgs/${orgId}/items/${code}/feedback`);
    return response.data;
  },
  async addOrgFeedback(orgId: string, code: string, message: string, files?: File[]): Promise<UatFeedbackDto> {
    const formData = new FormData();
    formData.append('message', message);
    if (files) files.forEach(f => formData.append('files', f));
    const token = useAuthStore.getState().accessToken;
    const resp = await axios.post<ApiResponse<UatFeedbackDto>>(
      `${projectConfig.api.baseUrl}${ADMIN_BASE}/orgs/${orgId}/items/${code}/feedback`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` } }
    );
    return resp.data.data;
  },
  async reviewItem(orgId: string, code: string, status: string, note?: string): Promise<UatItemDto> {
    const response = await put<ApiResponse<UatItemDto>>(
      `${ADMIN_BASE}/orgs/${orgId}/items/${code}/review`,
      { status, note }
    );
    return response.data;
  },
};
